import React, { Dispatch, ReactNode, SetStateAction, useRef } from 'react';
import { TableCell } from '../../../../components/organisms/Table';
import { Tooltip, TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { daysAndMonthsWithLeadingZero, months, normalizeDate } from './useCalendar';
import { CalendarDayInfoType, EventType } from '../../CalendarTypes';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import bem from '../../../../lib/bemUtils/bem';

type CalendarDayType = {
  weekDay: number;
  row: number;
  eventListId: string;
  eventsToShow: EventType[];
  setEventsToShow: Dispatch<SetStateAction<EventType[]>>;
  onClickEvent: (currentDate: string) => void;
  getClassOfCalendarDay: (calendarDayInfo: CalendarDayInfoType) => any;
  getCalendarDayInfo: (position: number) => CalendarDayInfoType;
};
export function CalendarDay({
  weekDay,
  row,
  getCalendarDayInfo,
  eventsToShow,
  getClassOfCalendarDay,
  setEventsToShow,
  onClickEvent,
  eventListId,
}: CalendarDayType): ReactNode {
  const { t } = useSonTranslation('Calendar');
  const calendarDayInfo = getCalendarDayInfo(row * 7 + weekDay);
  const isEventsOpen = eventsToShow.length > 0 && calendarDayInfo.date === normalizeDate(eventsToShow[0].date);
  const whatMonthIsToday = Number(calendarDayInfo.date.split('-')[1]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const datetime = `${daysAndMonthsWithLeadingZero(whatMonthIsToday)}-${daysAndMonthsWithLeadingZero(calendarDayInfo.day)}`;

  const addLeadingZero = (day: number) => {
    return day.toString().padStart(2, '0');
  };

  const titleTextAndInvisibleName = () => {
    const timeString = calendarDayInfo.date;
    const month = Number(timeString.split('-')[1]) - 1;

    if (calendarDayInfo.eventsCount) {
      if (calendarDayInfo.eventsCount > 1) {
        return t('eventsCount', {
          count: calendarDayInfo.eventsCount,
          day: calendarDayInfo.day,
          month: t(`months.${months[month]}`),
        });
      }
      return t('eventCount', {
        count: calendarDayInfo.eventsCount,
        day: calendarDayInfo.day,
        month: t(`months.${months[month]}`),
      });
    }
  };

  const today = calendarDayInfo.isCurrentDay ? ' - ' + t('today') : '';

  return (
    <TableCell
      key={weekDay}
      customTableDataClass={`font-regular-14-14 calendar__tabledata${getClassOfCalendarDay(calendarDayInfo)}`}
    >
      {/* <div> needed to stretch the table cell */}
      <div className="calendar__cell-container">
        <p className={bem('calendar__date', calendarDayInfo.isCurrentDay ? 'today' : null)}>
          <span className="calendar__day-info">
            {/* the hidden text for screen readers */}
            <time dateTime={datetime} className="invisible">
              {`${calendarDayInfo.day}. ${t(`months.${months[whatMonthIsToday - 1]}`)}${today}`}
            </time>
            {/* the visual text */}
            <span aria-hidden="true" role="presentation" className="calendar__day">
              {addLeadingZero(calendarDayInfo.day)}
              {calendarDayInfo.eventsCount > 0 && <span className="calendar__event-indicator"></span>}
            </span>
          </span>
          {calendarDayInfo.eventsCount > 0 && (
            <Tooltip attachTo={buttonRef} hideTooltipFromScreenReader hideOnClick placement={TooltipPlacement.TOP}>
              <button
                className={bem('calendar__button', 'open')}
                onClick={isEventsOpen ? () => setEventsToShow([]) : () => onClickEvent(calendarDayInfo.date)}
                aria-controls={eventListId}
                aria-expanded={isEventsOpen}
                type="button"
                ref={buttonRef}
                title={titleTextAndInvisibleName()}
              >
                <span className="invisible">{titleTextAndInvisibleName()}</span>
              </button>
            </Tooltip>
          )}
          {isEventsOpen && (
            <span className="calendar__fake-x-button">
              <SVG svgSpriteId={Icons.DELETE_X_SMALL} />
            </span>
          )}
        </p>
      </div>
    </TableCell>
  );
}
